import { useAuth0 } from '@auth0/auth0-react'
import { SortOrder, useCompaniesLazyQuery } from '@generated/graphql'
import { setCompanies, setCompaniesLoading } from '@store/slices/companies'
import { defaultLanguageIdSelector } from '@store/slices/languages/selectors'
import { useAppDispatch, useAppSelector } from '@store/typedHooks'
import { useCallback, useEffect } from 'react'

export function useFetchCompanies(refetchOnly: boolean = false) {
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAuth0()
  const defaultLanguageId = useAppSelector(defaultLanguageIdSelector)

  const [queryCompanies, { refetch, called }] = useCompaniesLazyQuery({
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ companies }) => {
      dispatch(setCompanies(companies))
    },
    onError: error => {
      console.error('Unable to load companies', error)
      dispatch(setCompaniesLoading(false))
    },
  })

  useEffect(() => {
    if (!isAuthenticated || !defaultLanguageId || refetchOnly) {
      return
    }

    dispatch(setCompaniesLoading(true))
    queryCompanies({
      variables: getQueryVariables(defaultLanguageId),
    })
  }, [
    isAuthenticated,
    queryCompanies,
    defaultLanguageId,
    refetchOnly,
    dispatch,
  ])

  const fetchCompanies = useCallback(async () => {
    dispatch(setCompaniesLoading(true))
    if (called) {
      await refetch(getQueryVariables(defaultLanguageId))
    } else {
      await queryCompanies({
        variables: getQueryVariables(defaultLanguageId),
      })
    }
  }, [called, defaultLanguageId, dispatch, queryCompanies, refetch])

  if (refetchOnly) {
    return fetchCompanies
  }
}

const getQueryVariables = defaultLanguageId => {
  return {
    defaultLanguageId,
    orderBy: { name: SortOrder.Asc },
  }
}
